import { Question, Resource, Response } from '@esg/esg-global-types';
import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Theme,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React from 'react';
import SDGIcon from '../../shared/decoration/SDGIcon';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';
import { BodyText } from '../../shared/typography/PrimaryTypography';
import { getGRIResourceLink, getSDGResourceLink } from '../../../lib/readiness_gauge/question';

interface KPIQuestionProps {
  question: Question;
  response?: Response;
  save_loading: boolean;
  show_gri_refs: boolean;
  show_sdgs: boolean;
  gri_resources: Array<Resource>;
  sdg_resources: Array<Resource>;
  handleUpdateRespones: (response: Response) => void;
}

/**
 * Displays a Question to be answered for the Readiness Gauge Questionnaire.
 * @param {Question} question Question to be answered.
 * @param {response | undefined} response Response to Question if given by user.
 * @param {boolean} save_loading The saving state of the Questionnaire to disable more inputs.
 * @param {Array<Resource>} gri_resources List of GRI related Resource Centre resources to search through for relevant compliance reference link.
 * @param {Array<Resource>} sdg_resources List of SDG related Resource Centre resources to search through for relevant sdg reference links.
 * @param {(response: Response): void} handleUpdateResponses Update Response list in parent on user input.
 * @returns {JSX.Element}
 */
const KPIQuestion = ({
  question,
  response,
  save_loading,
  show_gri_refs,
  show_sdgs,
  gri_resources,
  sdg_resources,
  handleUpdateRespones
}: KPIQuestionProps) => {
  const theme: Theme = useTheme();

  // Update response in memory from radio button click.
  const handleResponse = (value: string): void => {
    const new_response: Response = {
      id: response?.id ?? '',
      question: question,
      response: Number(value)
    };
    handleUpdateRespones(new_response);
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Grid size={0.5}>
          <Tooltip title={<Typography fontSize="0.9rem">{question.description}</Typography>} arrow>
            <InfoOutlinedIcon color="primary" />
          </Tooltip>
        </Grid>
        <Grid size={9.5} sx={{ textAlign: 'left' }}>
          <Typography sx={{ my: 0.5 }}>{question.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {show_sdgs && question.sdg_references.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {question.sdg_references.map(
                  (sdg_reference: string) =>
                    !isNaN(Number(sdg_reference)) && (
                      <a
                        key={`${question.id}_${sdg_reference}`}
                        href={getSDGResourceLink(sdg_reference, sdg_resources)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <SDGIcon sdg={Number(sdg_reference)} width={40} />
                      </a>
                    )
                )}
              </Box>
            )}
            {show_gri_refs && question.compliance_references.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {question.compliance_references.map((compliance_ref: string) => (
                  <a
                    key={`${question.id}_${compliance_ref}`}
                    href={getGRIResourceLink(compliance_ref, gri_resources)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: theme.palette.primary.main,
                      textDecoration: 'underline',
                      fontSize: '0.9rem'
                    }}
                  >
                    <BodyText weight="Medium" sx={{ display: 'inline' }}>
                      {compliance_ref.replaceAll('GRI ', '')}
                    </BodyText>
                  </a>
                ))}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid size={2} sx={{ textAlign: 'center' }}>
          <RadioGroup
            row
            value={response?.response.toString() ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleResponse(event.target.value)
            }
          >
            <FormControlLabel
              sx={{ width: '40%', m: 0, justifyContent: 'center' }}
              value="1"
              control={
                <Radio
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.8rem'
                    }
                  }}
                  disabled={save_loading}
                />
              }
              label=""
            />
            <FormControlLabel
              sx={{ width: '40%', m: 0, justifyContent: 'center' }}
              value="0"
              control={
                <Radio
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.8rem'
                    }
                  }}
                  disabled={save_loading}
                />
              }
              label=""
            />
          </RadioGroup>
        </Grid>
        <Grid size={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <PrimaryDivider opacity={0.2} sx={{ width: '90%' }} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default KPIQuestion;
